import { useMemo, useState } from "react";
import { arrayOf, object, string } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { isMobile } from "react-device-detect";
import { parseWorkTypesOrMetadata, universalDateParser } from "@utils";
import { useActiveProjectStore, useCheckProjectAccess, useStores } from "@hooks";
import { DescriptionEditor, ProjectChat, Header, ActionPanel } from "@dialogs/ProjectEditionDialog/components";
import { InfoLabel, ClearButton } from "@components";
import { Grid } from "@material-ui/core";
import { Comment } from "@assets";
import { makeStyles } from "@material-ui/styles";

export const ActiveProjectDetailsView = observer(({
  projectUuid,
  teamMembers,
}) => {

  const { userStore } = useStores();
  const activeProjects = useActiveProjectStore();
  const { t } = useTranslation();
  const classes = useStyle(isMobile);

  const [ showCommentInput, setShowCommentInput ] = useState(false);

  const {
    id,
    uuid,
    createdAt,
    dueOn,
    name,
    tags,
    metadata,
    users,
    content,
    attachments,
    type,
    status,
    btSetUp,
    btExported,
    hasComments,
  } = activeProjects.currentProject;

  const { isOwner, isObserver } = useCheckProjectAccess(users);

  const deadline = useMemo(() => (
    dueOn ? universalDateParser(dueOn) : undefined
  ), [dueOn]);

  const created = useMemo(() => (
    createdAt ? universalDateParser(createdAt) : undefined
  ), [createdAt]);

  const isMini = useMemo(() => type === "mini", []);

  const pdfProposals = attachments
    ? attachments.filter((x) => x.type === "proposal")
    : [];

  const fileAttachments = attachments
    ? attachments.filter((x) => x.type !== "proposal")
    : [];

  const chatUsers = [
    ...users.observer,
    users.author,
    ...(users.owner && users.owner.id !== users.author.id ? [users.owner] : []),
  ].filter((u) => u.id !== userStore.data.id);
  
  const workTypes = useMemo(() => (
    parseWorkTypesOrMetadata(activeProjects.workTypes, tags, metadata)
  ), [metadata, tags, activeProjects.workTypes]);

  return (
    <>
      <Grid item container className={classes.noDialogHeader}>
        <Header
          uuid={uuid}
          name={name}
          description={content}
          isOwner={isOwner}
          isMini={isMini}
          isBusiness={userStore.isBusinessUser}
          deadline={deadline}
          created={created}
          status={status}
          id={id}
          projectBtSetUp={btSetUp}
          projectBtExported={btExported}
          hasComments={hasComments}
          showActionMenu
        />
      </Grid>
      <Grid
        item
        container
        direction="column"
        wrap="nowrap"
      >
        <InfoLabel label="views.active.popup.desc" />
        <DescriptionEditor
          description={content || ""}
          uuid={projectUuid}
          allowEdition={isOwner}
        />
        <ActionPanel
          uuid={uuid}
          isOwner={isOwner}
          isMini={isMini}
          attachments={isMini ? pdfProposals : fileAttachments}
          author={users.author}
          owner={users.owner}
          users={users.observer}
          teamMembers={teamMembers}
          workTypes={workTypes}
        />
        <InfoLabel
          label="views.active.popup.activity"
          action={
            isObserver ? (
              <ClearButton
                icon={<Comment />}
                onClick={() => setShowCommentInput(true)}
              >
                {t("views.active.popup.add_comment")}
              </ClearButton>
            ) : undefined
          }
        />
        <ProjectChat
          projectUuid={uuid}
          noAdding={!isObserver}
          users={chatUsers}
          teamMembers={teamMembers}
          showCommentInput={showCommentInput}
          onCommentCommit={() => setShowCommentInput(false)}
        />
      </Grid>
    </>
  );
});

ActiveProjectDetailsView.propTypes = {
  projectUuid: string.isRequired,
  teamMembers: arrayOf(object).isRequired,
};

const useStyle = makeStyles((theme) => ({
  header: {
    margin: "0 -16px 16px",
    padding: 16,
    width: "calc(100% + 32px)",
    backgroundColor: theme.props.backgroundSecondary,
    borderBottom: "1px solid " + theme.props.border,
  },
}));